import React, { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, DownloadIcon, SettingsIcon, TrendingUpIcon, AlertCircleIcon, CheckCircle2Icon, InfoIcon, FileTextIcon, XIcon } from 'lucide-react';
import { BRAND } from '../../../shared/config/brand';
import { formatCurrencyAmount } from '../../../theme/tokens';
import { getFacilityById, generateRepaymentLedger, mockFacilities } from '../mocks/financingData';
type Tab = 'overview' | 'repayment' | 'details' | 'settings';
type PayEarlyStep = 'settle' | 'success';
const formatMYR = (amount: number) => formatCurrencyAmount(amount, {
  currency: 'MYR'
});
// Estimated daily deduction is an estimate; round it so the "~" and the
// precision don't contradict each other (e.g. "~MYR 500/day").
const formatRoughDaily = (v: number) => `~${formatMYR(Math.round(v / 50) * 50)}/day`;
export function FacilityDetail() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  // Early-settlement (ibra') flow state
  const [showPayEarly, setShowPayEarly] = useState(false);
  const [payEarlyStep, setPayEarlyStep] = useState<PayEarlyStep>('settle');
  // Settings state
  const [holdbackRate, setHoldbackRate] = useState<number>(10);
  const [showSettingsConfirm, setShowSettingsConfirm] = useState(false);
  const [pendingHoldbackRate, setPendingHoldbackRate] = useState<number>(10);
  const facility = getFacilityById(id || '') || mockFacilities[0];
  const ledger = useMemo(() => generateRepaymentLedger(facility), [facility]);
  // Murabaha fixed-profit economics. Ibra' (rebate) waives the unaccrued
  // portion of the fixed profit when the merchant settles early.
  const fixedProfit = facility.totalRepayment - facility.principal;
  const ibraRebate = Math.round(fixedProfit * (1 - facility.repaidPercent / 100));
  const settleToday = facility.outstanding - ibraRebate;
  const pricingModelLabel = facility.pricingModel.replace('Fixed factor rate', 'Fixed-profit murabaha');
  const openPayEarly = () => {
    setPayEarlyStep('settle');
    setShowPayEarly(true);
  };
  const handlePayEarlyClose = () => {
    setShowPayEarly(false);
    setPayEarlyStep('settle');
  };
  const tabs: {
    key: Tab;
    label: string;
  }[] = [{
    key: 'overview',
    label: 'Overview'
  }, {
    key: 'repayment',
    label: 'Repayment ledger'
  }, {
    key: 'details',
    label: 'Details'
  }, {
    key: 'settings',
    label: 'Settings'
  }];
  return <div className="max-w-5xl mx-auto pb-12" data-id="element-4561" data-mp="1qyriovl4a2oj">
      <button onClick={() => navigate('/bills/financing')} className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 mb-6" data-id="element-4562" data-mp="1vymdha80jcev">
        <ArrowLeftIcon size={16} data-id="element-4563" data-mp="1l4l99wnxrdi" />
        Back to Financing
      </button>

      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 mb-8" data-id="element-4564" data-mp="3q79gesti0rln">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-6" data-id="element-4565" data-mp="3lgt11364avwn">
          <div data-id="element-4566" data-mp="3lezudnvi8bon">
            <div className="flex items-center gap-3 mb-2" data-id="element-4567" data-mp="3ghgbubrrxcqw">
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-4568" data-mp="3ghzkf1vjaos9">
                {facility.id}
              </h1>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-green-700 bg-green-50 font-medium text-sm" data-id="element-4569" data-mp="3bgsc91phbnl2">
                {facility.status}
              </span>
            </div>
            <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm" data-id="element-4570" data-mp="803hecz9e2b8">
              <div data-id="element-4571" data-mp="318o2h3o0vcv">
                <span className="text-gray-500" data-id="element-4572" data-mp="34wf99ln15of">Outstanding balance:</span>{' '}
                <span className="font-semibold text-gray-900" data-id="element-4573" data-mp="3v62vgpofoqnx">
                  {formatMYR(facility.outstanding)}
                </span>
              </div>
              <div data-id="element-4574" data-mp="2jqxnb65j9zwx">
                <span className="text-gray-500" data-id="element-4575" data-mp="2jqe83x79ppml">Projected close:</span>{' '}
                <span className="font-semibold text-gray-900" data-id="element-4576" data-mp="bi903grgt4dy">
                  {facility.projectedCloseDate}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-4577" data-mp="1kuw4gmqa82j">
            <button onClick={openPayEarly} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[var(--brand-primary)] text-white text-sm font-medium hover:bg-[var(--brand-primary-hover)] transition-colors" data-id="element-4578" data-mp="1m612jrwtlv8">
              <TrendingUpIcon size={16} data-id="element-4579" data-mp="z8lhrjciifhq" />
              Pay early
            </button>
            <button onClick={() => setActiveTab('settings')} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors" data-id="element-4580" data-mp="l8u63p9gxb6w">
              <SettingsIcon size={16} data-id="element-4581" data-mp="35xi1b9f3b269" />
              Settings
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-8" data-id="element-4582" data-mp="32gcdwyqvpqgq">
        <nav className="flex gap-8" data-id="element-4583" data-mp="32f1169fgh8dh">
          {tabs.map(tab => <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`pb-3 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.key ? 'border-[var(--brand-primary)] text-[var(--brand-primary)]' : 'border-transparent text-gray-500 hover:text-gray-900'}`} data-id="element-4584" data-mp="2xgr5zytqe7rv">
              {tab.label}
            </button>)}
        </nav>
      </div>

      {/* Tab content */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8" data-id="element-4585" data-mp="2tg764nhrwq21">
        {activeTab === 'overview' && <div className="space-y-8" data-id="element-4586" data-mp="2ooxgvo04zoqo">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-4587" data-mp="2onw85q6lfec4">
              <div className="md:col-span-2" data-id="element-4588" data-mp="20z3579rk0680">
                <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-4589" data-mp="20xs45hphtckl">
                  Repayment progress
                </h3>
                <div className="flex justify-between items-end mb-2" data-id="element-4590" data-mp="2lp73lrvttxcy">
                  <span className="text-3xl font-bold text-gray-900" data-id="element-4591" data-mp="2lmupxqx3q95p">
                    {formatMYR(facility.totalRepayment - facility.outstanding)}
                    <span className="text-lg text-gray-400 font-normal" data-id="element-4592" data-mp="2ugzbbutgusdu">
                      {' '}
                      / {formatMYR(facility.totalRepayment)}
                    </span>
                  </span>
                  <div className="text-sm font-medium text-gray-600" data-id="element-4593" data-mp="22r4iho6egkkf">
                    {facility.repaidPercent}% repaid
                  </div>
                </div>
                <div className="h-3 bg-gray-100 rounded-full overflow-hidden mb-4" data-id="element-4594" data-mp="2br16dnkytfef">
                  <div className="h-full bg-[var(--brand-primary)]" style={{
                width: `${facility.repaidPercent}%`
              }} data-id="element-4595" data-mp="2gpujvg0956el" />
                </div>
                <div className="flex items-center gap-2 text-sm" data-id="element-4596" data-mp="3s3ybwgna4kh0">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-green-700 bg-green-50 font-medium" data-id="element-4597" data-mp="1gheccow7ek4t">
                    <CheckCircle2Icon size={14} data-id="element-4598" data-mp="1bjm76t6jt1io" />
                    At your current sales pace
                  </span>
                  <span className="text-gray-500" data-id="element-4599" data-mp="16q9y5recr3b1">
                    Projected to close {facility.projectedCloseDate}
                  </span>
                </div>

                {ibraRebate > 0 && <button onClick={openPayEarly} className="mt-5 w-full flex items-center justify-between gap-3 p-4 rounded-lg border border-[var(--brand-primary)] bg-red-50/40 text-left hover:bg-red-50 transition-colors" data-id="element-4600" data-mp="xshgigl5rjkh">
                    <div className="flex items-center gap-2" data-id="element-4601" data-mp="iv75f8cfgfpa">
                      <TrendingUpIcon size={18} className="text-[var(--brand-primary)] flex-shrink-0" data-id="element-4602" data-mp="2i6rln1zz1vlj" />
                      <span className="text-sm font-medium text-gray-900" data-id="element-4603" data-mp="2d63umgbqterb">
                        Settle early and save {formatMYR(ibraRebate)} (ibra')
                      </span>
                    </div>
                    <span className="text-sm font-semibold text-[var(--brand-primary)] whitespace-nowrap" data-id="element-4604" data-mp="1da8ypobiccdu">
                      Review
                    </span>
                  </button>}
              </div>

              <div className="space-y-6" data-id="element-4605" data-mp="13bjnufi13s73">
                <div data-id="element-4606" data-mp="13arsn5scfwhc">
                  <div className="text-sm text-gray-500 mb-1" data-id="element-4607" data-mp="13efnq9dfsqy6">
                    Holdback rate
                  </div>
                  <div className="text-xl font-bold text-gray-900" data-id="element-4608" data-mp="ubocoaoh3x72">
                    {facility.holdbackRate}%
                  </div>
                </div>
                <div data-id="element-4609" data-mp="yd9ri1kkiljw">
                  <div className="text-sm text-gray-500 mb-1" data-id="element-4610" data-mp="2opfrrppkn99w">
                    Avg. daily deduction
                  </div>
                  <div className="text-xl font-bold text-gray-900" data-id="element-4611" data-mp="2srasot3lbkn5">
                    {formatRoughDaily(facility.avgDailyDeduction)}
                  </div>
                </div>
              </div>
            </div>
          </div>}

        {activeTab === 'repayment' && <div className="space-y-6" data-id="element-4612" data-mp="qt7c59kxg1dr">
            <div className="bg-blue-50 p-4 rounded-lg flex items-start gap-3 border border-blue-100" data-id="element-4613" data-mp="qu8xc6vnq497">
              <InfoIcon size={18} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-4614" data-mp="m1z34eniei14" />
              <div className="text-sm text-blue-800" data-id="element-4615" data-mp="10trd0b4jb3vg">
                <p className="font-medium mb-1" data-id="element-4616" data-mp="vrilzkb82bvz">Repayment is automatic</p>
                <p data-id="element-4617" data-mp="19sv0draqi5fr">
                  A {facility.holdbackRate}% holdback is applied to your daily
                  settlement volume and credited toward this facility
                  automatically. Your total payable is fixed at{' '}
                  {formatMYR(facility.totalRepayment)} — only the daily
                  deduction varies with your sales.
                </p>
              </div>
            </div>

            <div className="flex justify-between items-center" data-id="element-4618" data-mp="1irozlf86ba0l">
              <h3 className="text-lg font-bold text-gray-900" data-id="element-4619" data-mp="1drj41nys93d8">
                Repayment ledger
              </h3>
              <div className="text-sm text-gray-500" data-id="element-4620" data-mp="19pfp2pcwngn7">
                Next deduction:{' '}
                <strong className="text-gray-900" data-id="element-4621" data-mp="14r4h6k667g7m">Tomorrow</strong> (
                {formatRoughDaily(facility.avgDailyDeduction)})
              </div>
            </div>

            <div className="overflow-x-auto" data-id="element-4622" data-mp="21j7z3c2zo5xv">
              <table className="w-full text-left border-collapse" data-id="element-4623" data-mp="1xfa33a8ynb91">
                <thead data-id="element-4624" data-mp="1sotovnxbfllm">
                  <tr className="bg-gray-50 border-y border-gray-200 text-xs uppercase tracking-wider text-gray-500" data-id="element-4625" data-mp="1soa84ajej396">
                    <th className="px-4 py-3 font-medium" data-id="element-4626" data-mp="1nonpflkt0usz">Date</th>
                    <th className="px-4 py-3 font-medium" data-id="element-4627" data-mp="2l4uodzsl6klh">Batch</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-4628" data-mp="2l77e4z4l5z93">GMV</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-4629" data-mp="2gctf8mqpt9zo">
                      Deducted ({facility.holdbackRate}%)
                    </th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-4630" data-mp="2sodq3n9m12je">
                      Outstanding
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-4631" data-mp="2xq30r1sx7e6y">
                  {ledger.map(row => <tr key={row.id} className="hover:bg-gray-50" data-id="element-4632" data-mp="31mz558fvhzff">
                      <td className="px-4 py-3 text-sm text-gray-900" data-id="element-4633" data-mp="36nvdpzb669r6">
                        {new Date(row.date).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 font-mono" data-id="element-4634" data-mp="3gdy5xxagvjog">
                        {row.batchRef}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 text-right" data-id="element-4635" data-mp="3lfdsjtsaly8y">
                        {formatMYR(row.gmv)}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right" data-id="element-4636" data-mp="3ud7pn6xvsvx6">
                        {formatMYR(row.deducted)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 text-right" data-id="element-4637" data-mp="1qa4li86wuicw">
                        {formatMYR(row.runningBalance)}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>}

        {activeTab === 'details' && <div className="space-y-8" data-id="element-4638" data-mp="2dynyh10wnjbb">
            <h3 className="text-lg font-bold text-gray-900" data-id="element-4639" data-mp="2e1tl89m2a93v">
              Facility details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-12" data-id="element-4640" data-mp="292ysaeudr461">
              <div data-id="element-4641" data-mp="2mzm23i7flotd">
                <div className="text-sm text-gray-500 mb-1" data-id="element-4642" data-mp="2my17xtp147w1">Principal</div>
                <div className="font-medium text-gray-900" data-id="element-4643" data-mp="2i23qfk214vho">
                  {formatMYR(facility.principal)}
                </div>
              </div>
              <div data-id="element-4644" data-mp="nnt17ahivzbd">
                <div className="text-sm text-gray-500 mb-1" data-id="element-4645" data-mp="nocl2wqqgxs1">Pricing model</div>
                <div className="font-medium text-gray-900" data-id="element-4646" data-mp="2za4qzj7rvagl">
                  {pricingModelLabel}
                </div>
              </div>
              <div data-id="element-4647" data-mp="3497wt5vktkpm">
                <div className="text-sm text-gray-500 mb-1" data-id="element-4648" data-mp="34bu4d5igw07u">Total payable</div>
                <div className="font-medium text-gray-900" data-id="element-4649" data-mp="2qht8qkmtatbp">
                  {formatMYR(facility.totalRepayment)}
                </div>
              </div>
              <div data-id="element-4650" data-mp="2gjlw2dzk2xfi">
                <div className="text-sm text-gray-500 mb-1" data-id="element-4651" data-mp="2gj3ur2y1dt0q">Fixed profit</div>
                <div className="font-medium text-gray-900" data-id="element-4652" data-mp="2ljrinfqyxtqq">
                  {formatMYR(fixedProfit)}
                </div>
              </div>
              <div data-id="element-4653" data-mp="27l9y1qwd0xd1">
                <div className="text-sm text-gray-500 mb-1" data-id="element-4654" data-mp="27lthxd5klvtp">Holdback rate</div>
                <div className="font-medium text-gray-900" data-id="element-4655" data-mp="2blbzbyy6nors">
                  {facility.holdbackRate}% daily
                </div>
              </div>
              <div data-id="element-4656" data-mp="9u7997bwew9k">
                <div className="text-sm text-gray-500 mb-1" data-id="element-4657" data-mp="9snujl5w4t9w">Start date</div>
                <div className="font-medium text-gray-900" data-id="element-4658" data-mp="3h8yuysdem09n">
                  {facility.startDate}
                </div>
              </div>
              <div data-id="element-4659" data-mp="3c9vor4loto03">
                <div className="text-sm text-gray-500 mb-1" data-id="element-4660" data-mp="3c79h74ysr8hv">
                  Disbursement account
                </div>
                <div className="font-medium text-gray-900" data-id="element-4661" data-mp="32bew8373p1bx">
                  {facility.disbursementAccount}
                </div>
              </div>
              <div data-id="element-4662" data-mp="2yb4ejt9y4mh8">
                <div className="text-sm text-gray-500 mb-1" data-id="element-4663" data-mp="2ybnwvb3idcvs">
                  Target repayment window
                </div>
                <div className="font-medium text-gray-900" data-id="element-4664" data-mp="2ojrpf9fj8aew">
                  {facility.targetRepaymentWindow}
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-gray-200" data-id="element-4665" data-mp="2jikej1i3ev40">
              <h4 className="text-sm font-bold text-gray-900 mb-4" data-id="element-4666" data-mp="2ekivggko2vsj">
                Documents
              </h4>
              <div className="space-y-3" data-id="element-4667" data-mp="2ala2hdxgb1y1">
                <button className="w-full flex items-center justify-between p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors" data-id="element-4668" data-mp="q1y91j2rktfo">
                  <div className="flex items-center gap-3" data-id="element-4669" data-mp="u28dgrawrbsf">
                    <FileTextIcon size={18} className="text-gray-400" data-id="element-4670" data-mp="z2w43bv85qm4" />
                    <span className="font-medium text-gray-900" data-id="element-4671" data-mp="z0jqtc0ew4fe">
                      Murabaha financing agreement
                    </span>
                  </div>
                  <DownloadIcon size={18} className="text-gray-500" data-id="element-4672" data-mp="6f9yjy2fsbwm" />
                </button>
                <button className="w-full flex items-center justify-between p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors" data-id="element-4673" data-mp="bdto08g8o5tq">
                  <div className="flex items-center gap-3" data-id="element-4674" data-mp="l2v19qveg940">
                    <FileTextIcon size={18} className="text-gray-400" data-id="element-4675" data-mp="l4fvffdsxq1c" />
                    <span className="font-medium text-gray-900" data-id="element-4676" data-mp="3jtn1sfj6r373">
                      Ibra' / early settlement policy
                    </span>
                  </div>
                  <DownloadIcon size={18} className="text-gray-500" data-id="element-4677" data-mp="3gr8t0l4t8tvj" />
                </button>
                <button className="w-full flex items-center justify-between p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors" data-id="element-4678" data-mp="3cpncmpt2hxgl">
                  <div className="flex items-center gap-3" data-id="element-4679" data-mp="3qfqtt2d3b0z6">
                    <FileTextIcon size={18} className="text-gray-400" data-id="element-4680" data-mp="3li8p5o63v7wz" />
                    <span className="font-medium text-gray-900" data-id="element-4681" data-mp="3lkbfk9b67328">
                      Monthly statements
                    </span>
                  </div>
                  <DownloadIcon size={18} className="text-gray-500" data-id="element-4682" data-mp="2truh48krwpt8" />
                </button>
              </div>
            </div>
          </div>}

        {activeTab === 'settings' && <div className="space-y-8 max-w-2xl" data-id="element-4683" data-mp="32qfxpy6opurd">
            <h3 className="text-lg font-bold text-gray-900" data-id="element-4684" data-mp="32tu0h7j9g5zc">
              Facility settings
            </h3>

            <div className="space-y-4" data-id="element-4685" data-mp="i18q1cu6m7ld">
              <h4 className="text-sm font-bold text-gray-900" data-id="element-4686" data-mp="i075mhra08qz">
                Disbursement account
              </h4>
              <div className="p-4 rounded-lg border border-gray-200 bg-gray-50" data-id="element-4687" data-mp="1zxbwzacdbqaj">
                <div className="text-sm text-gray-500 mb-1" data-id="element-4688" data-mp="24yrg2wuzjmqa">Settles from</div>
                <div className="font-medium text-gray-900" data-id="element-4689" data-mp="1qyys39aufkm0">
                  {facility.disbursementAccount}
                </div>
                <p className="text-xs text-gray-500 mt-2" data-id="element-4690" data-mp="1vxtk91upalis">
                  To change your disbursement account, please contact{' '}
                  {BRAND.name} support.
                </p>
              </div>
            </div>

            <div className="space-y-4" data-id="element-4691" data-mp="2nqafyutpqko3">
              <h4 className="text-sm font-bold text-gray-900" data-id="element-4692" data-mp="29xukqg71zqxq">Holdback rate</h4>
              <p className="text-sm text-gray-600" data-id="element-4693" data-mp="2evw12td3hc5i">
                Increasing your holdback rate repays the facility faster but
                deducts more from each day's settlement.
              </p>
              <p className="text-sm text-gray-600" data-id="element-4694" data-mp="37cyq2eq4p06m">
                Your total payable is fixed and does not change with holdback
                rate — a higher holdback only shortens the timeline.
              </p>
              <div className="flex items-center gap-4" data-id="element-4695" data-mp="12iftnl1aefq5">
                <select value={holdbackRate} onChange={e => {
              setPendingHoldbackRate(Number(e.target.value));
              setShowSettingsConfirm(true);
            }} className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-900" data-id="element-4696" data-mp="12hnz8djfeo1g">
                  <option value={10} data-id="element-4697" data-mp="1lai063k7sfmo">10% (Contracted)</option>
                  <option value={15} data-id="element-4698" data-mp="1gacf568g9rdk">15% (Accelerated)</option>
                  <option value={20} data-id="element-4699" data-mp="1gcqbdiev99mp">20% (Aggressive)</option>
                </select>
              </div>

              {showSettingsConfirm && <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mt-4" data-id="element-4700" data-mp="4wrw2wt3hrd1">
                  <h5 className="text-sm font-bold text-amber-900 mb-2 flex items-center gap-2" data-id="element-4701" data-mp="tvjpnu5erhj">
                    <AlertCircleIcon size={16} data-id="element-4702" data-mp="2ci9tnzyvd1jk" />
                    Increase holdback rate?
                  </h5>
                  <p className="text-sm text-amber-800 mb-4" data-id="element-4703" data-mp="2gk3r7zdrppfi">
                    This will change your daily holdback to{' '}
                    {pendingHoldbackRate}%, taking effect on your next
                    settlement.
                  </p>
                  <div className="flex gap-3" data-id="element-4704" data-mp="2v7ylvlbc63v8">
                    <button onClick={() => setShowSettingsConfirm(false)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors" data-id="element-4705" data-mp="2v6x12p4iq30b">
                      Cancel
                    </button>
                    <button onClick={() => {
                setHoldbackRate(pendingHoldbackRate);
                setShowSettingsConfirm(false);
              }} className="px-4 py-2 bg-[var(--brand-primary)] text-white rounded-lg text-sm font-medium hover:bg-[var(--brand-primary-hover)] transition-colors" data-id="element-4706" data-mp="396ya4ruxsgqv">
                      Confirm
                    </button>
                  </div>
                </div>}
            </div>
          </div>}
      </div>

      {/* Pay Early modal */}
      <AnimatePresence data-id="element-4707" data-mp="1dd77bypfqrdc">
        {showPayEarly && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-id="element-4708" data-mp="1hdzjazpozadb">
            <motion.div initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.96
        }} className="bg-white rounded-xl shadow-xl w-full max-w-lg" data-id="element-4709" data-mp="1w9p9zb5tobod">
              <div className="p-6 border-b border-gray-100 flex justify-between items-center" data-id="element-4710" data-mp="2af7mqepqxsqz">
                <h2 className="text-xl font-bold text-gray-900" data-id="element-4711" data-mp="26fqghj2hwrkr">
                  Settle facility early
                </h2>
                <button onClick={handlePayEarlyClose} className="text-gray-400 hover:text-gray-600" data-id="element-4712" data-mp="21hf7tbnxsn44">
                  <XIcon size={24} data-id="element-4713" data-mp="2u3q9yqr6yw31" />
                </button>
              </div>

              <div className="p-6" data-id="element-4714" data-mp="2p56mglwy9c8k">
                {payEarlyStep === 'settle' && <div className="space-y-6" data-id="element-4715" data-mp="2p6zti2bh5yh3">
                    <div className="rounded-xl border border-gray-200 overflow-hidden" data-id="element-4716" data-mp="2k50pcq7wuxj3">
                      <div className="flex justify-between items-center px-5 py-4" data-id="element-4717" data-mp="1dq9rywhzxutt">
                        <span className="text-sm text-gray-600" data-id="element-4718" data-mp="18qdoqzh4i7fb">
                          Outstanding balance
                        </span>
                        <span className="text-sm font-medium text-gray-900" data-id="element-4719" data-mp="1a8lq09399i7k">
                          {formatMYR(facility.outstanding)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center px-5 py-4 border-t border-gray-100" data-id="element-4720" data-mp="116w0x9wuivcy">
                        <span className="text-sm text-gray-600" data-id="element-4721" data-mp="164ns21po2dg3">
                          Ibra' rebate (unearned profit waived)
                        </span>
                        <span className="text-sm font-medium text-green-700" data-id="element-4722" data-mp="w8kqawyq8gto">
                          −{formatMYR(ibraRebate)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center px-5 py-4 bg-red-50/50 border-t border-gray-100" data-id="element-4723" data-mp="mhzvlkm6bwg1">
                        <span className="text-base font-semibold text-gray-900" data-id="element-4724" data-mp="8jrssw2gi0wy">
                          Settle today for
                        </span>
                        <span className="text-xl font-bold text-[var(--brand-primary)]" data-id="element-4725" data-mp="3q1fivqtetg0r">
                          {formatMYR(settleToday)}
                        </span>
                      </div>
                    </div>

                    <p className="text-sm text-gray-600" data-id="element-4726" data-mp="3uqqdqta97bw">
                      Because you're settling early, {BRAND.name} waives the
                      portion of profit not yet accrued (ibra'). You pay only
                      the financing principal outstanding plus profit accrued to
                      today.
                    </p>

                    <button onClick={() => setPayEarlyStep('success')} className="w-full py-4 bg-[var(--brand-primary)] text-white rounded-lg font-semibold hover:bg-[var(--brand-primary-hover)] transition-colors" data-id="element-4727" data-mp="7vj0sndw5i9r">
                      Settle facility — {formatMYR(settleToday)}
                    </button>

                    <p className="text-xs text-gray-500" data-id="element-4728" data-mp="rkti25tacb9z">
                      Ibra' is calculated on the unaccrued portion of the fixed
                      profit as at settlement date. Figures shown are
                      illustrative.
                    </p>
                  </div>}

                {payEarlyStep === 'success' && <div className="space-y-6 text-center py-8" data-id="element-4729" data-mp="2zr5chf3cfvus">
                    <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-4730" data-mp="2zqls7rq80vdl">
                      <CheckCircle2Icon size={32} className="text-green-600" data-id="element-4731" data-mp="225zlagbj323b" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900" data-id="element-4732" data-mp="2c1u7tmivhhbd">
                      Facility settled
                    </h3>
                    <p className="text-gray-600" data-id="element-4733" data-mp="2g3o3fge7nv4o">
                      Your early settlement of{' '}
                      <strong className="text-gray-900" data-id="element-4734" data-mp="3nfxzun5wgvds">
                        {formatMYR(settleToday)}
                      </strong>{' '}
                      has been scheduled, with an ibra' rebate of{' '}
                      <strong className="text-gray-900" data-id="element-4735" data-mp="1ftxy817822os">
                        {formatMYR(ibraRebate)}
                      </strong>
                      .
                    </p>
                    <button onClick={handlePayEarlyClose} className="w-full py-3 bg-[var(--brand-primary)] text-white rounded-lg font-medium hover:bg-[var(--brand-primary-hover)] transition-colors" data-id="element-4736" data-mp="1724fq984uhwk">
                      Done
                    </button>
                  </div>}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}